import { useMemo, useState } from "react";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { DiffView } from "./diff-view";
import { describeDiff, diffWords } from "./word-diff";

/**
 * Ask AI to revise the selected passage — and show the author exactly what would
 * change before anything touches the document.
 *
 * The confirmation is the feature, not a safety wrapper around it. An agent that
 * edits a memo directly is an agent whose changes have to be found later by
 * rereading the whole thing; a diff makes the change reviewable in the moment,
 * which is what lets it be trusted at all.
 *
 * The dialog is deliberately two steps rather than one: instruction first, then
 * the diff. Showing a proposal in the same box as the input invites accepting
 * without reading.
 */

/** Starting points, so the box is not blank. */
const PRESETS = [
  { label: "Tighten", instruction: "Tighten this. Same substance, fewer words." },
  {
    label: "More specific",
    instruction:
      "Make this more specific using only what the documents support. Where a figure is not stated, say it is not stated.",
  },
  {
    label: "Add the evidence",
    instruction:
      "Add the supporting evidence from the company's documents for the claims here. Do not add claims that are not supported.",
  },
  {
    label: "Neutral tone",
    instruction:
      "Rewrite this in a neutral, factual tone. Remove promotional language and attribute self-reported claims.",
  },
  {
    label: "As a table",
    instruction:
      "Present this as a table where that makes it easier to read. Keep the same facts.",
  },
];

export interface AskAiRevisionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The plain text of the selection, for the diff. */
  selectionText: string;
  /** The section the selection sits in, shown so the author knows what they hit. */
  sectionTitle: string;
  /** Asks the server for a revision. Returns the proposed HTML. */
  onRequest: (instruction: string) => Promise<{ html: string; text: string }>;
  /** Called only when the author accepts. Replaces the selection. */
  onAccept: (html: string) => void;
}

export function AskAiRevision({
  open,
  onOpenChange,
  selectionText,
  sectionTitle,
  onRequest,
  onAccept,
}: AskAiRevisionProps) {
  const [instruction, setInstruction] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<{ html: string; text: string } | null>(
    null
  );

  const diff = useMemo(
    () => (proposal ? diffWords(selectionText, proposal.text) : null),
    [proposal, selectionText]
  );

  const reset = () => {
    setInstruction("");
    setProposal(null);
    setError(null);
    setPending(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const request = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;

    setPending(true);
    setError(null);
    try {
      setProposal(await onRequest(trimmed));
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Couldn't revise that selection"
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            {proposal ? "Review the change" : "Edit with AI"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {proposal
              ? "Nothing has changed in your document yet. Accept to apply this to the selection."
              : `The selected text in "${sectionTitle}". Only the selection is changed.`}
          </DialogDescription>
        </DialogHeader>

        {!proposal && (
          <div className="space-y-3">
            <blockquote className="max-h-32 overflow-y-auto whitespace-pre-wrap border-l-2 border-border pl-3 text-xs text-muted-foreground">
              {selectionText}
            </blockquote>

            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    setInstruction(preset.instruction);
                    void request(preset.instruction);
                  }}
                  className={cn(
                    "rounded-full border border-border px-2.5 py-1 text-xs transition-colors",
                    "hover:border-primary hover:text-primary disabled:opacity-50"
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <Textarea
              autoFocus
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="Or describe the change you want — e.g. 'cut this to two sentences and drop the adjectives'"
              className="min-h-[80px] text-sm"
              onKeyDown={(event) => {
                // Enter sends; Shift+Enter is a newline. A textarea is here for
                // long instructions, but most are one line.
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void request(instruction);
                }
              }}
            />
          </div>
        )}

        {proposal && diff && (
          <div className="space-y-2">
            <div className="max-h-[45vh] overflow-y-auto rounded-md border border-border bg-muted/30 p-3">
              <DiffView spans={diff.spans} wordLevel={diff.wordLevel} />
            </div>
            <p className="text-xs text-muted-foreground">
              {diff.changed ? describeDiff(diff.spans) : "The AI left this unchanged."}
            </p>
          </div>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}

        <DialogFooter className="gap-2 sm:gap-2">
          {!proposal ? (
            <>
              <Button variant="ghost" size="sm" onClick={close} disabled={pending}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                disabled={!instruction.trim() || pending}
                onClick={() => void request(instruction)}
              >
                {pending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                Revise
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={close}
              >
                <X className="h-3.5 w-3.5" />
                Discard
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setProposal(null);
                  setError(null);
                }}
              >
                Try another instruction
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                // An unchanged proposal is not worth applying, and applying it
                // would still stamp the section as hand-edited.
                disabled={!diff?.changed}
                onClick={() => {
                  onAccept(proposal.html);
                  close();
                }}
              >
                <Check className="h-3.5 w-3.5" />
                Accept
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { PRESETS as ASK_AI_PRESETS };
